import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import * as api from '../../services/api';
import { Essay, EssayPurpose, Purpose, Transcription } from '../../types';
import SortableTable, { HeadCell } from '../common/SortableTable';
import Loading from '../Loading';

const TranscriptionsManagement = () => {
  const [transcriptions, setTranscriptions] = useState<Transcription[]>([]);
  const [essays, setEssays] = useState<Essay[]>([]);
  const [essayPurposes, setEssayPurposes] = useState<EssayPurpose[]>([]);
  const [purposes, setPurposes] = useState<Purpose[]>([]);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedTranscription, setSelectedTranscription] = useState<Transcription | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(true);

  useEffect(() => {
    setIsLoadingData(true);
    const fetchData = async () => {
      try {
        const [transcriptionsData, essaysData, essayPurposesData, purposesData] = await Promise.all(
          [
            api.fetchTranscriptions(),
            api.fetchEssays(),
            api.fetchEssayPurposes(),
            api.fetchPurposes(),
          ]
        );

        setTranscriptions(transcriptionsData);
        setEssays(essaysData);
        setEssayPurposes(essayPurposesData);
        setPurposes(purposesData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, []);

  const getTranscriptionDisplayLabel = useCallback(
    (transcription: Transcription): string => {
      const essay = essays.find(e => e.id === transcription.essayId);
      if (!essay) return transcription.versionName;

      const relevantEssayPurposes = essayPurposes.filter(ep => ep.essayId === essay.id);
      const purposeNames = relevantEssayPurposes
        .map(ep => purposes.find(p => p.id === ep.purposeId)?.name)
        .filter(name => !!name)
        .join(', ');

      let label = `${purposeNames || 'N/A'}`;
      label += ` - ${essay.learn_cycle_batch}`;
      label += ` - ${essay.paper_number}`;
      label += ` - ${transcription.versionName}`;

      return label;
    },
    [essays, essayPurposes, purposes]
  );

  const handleViewTranscription = (transcription: Transcription) => {
    setSelectedTranscription(transcription);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedTranscription(null);
  };

  const headCells: HeadCell<Transcription>[] = [
    {
      id: 'displayLabel',
      label: 'Transcription Label',
      numeric: false,
      sortable: true,
      renderCell: transcription => getTranscriptionDisplayLabel(transcription),
      getComparableValue: (transcription: Transcription) =>
        getTranscriptionDisplayLabel(transcription).toLowerCase(),
    },
    {
      id: 'creationDate',
      label: 'Creation Date',
      numeric: false,
      sortable: true,
      renderCell: transcription =>
        new Date(transcription.creationDate).toLocaleString(undefined, {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
      sortKey: 'creationDate',
    },
    {
      id: 'actions',
      label: '',
      numeric: false,
      sortable: false,
      renderCell: transcription => (
        <Button size="small" color="primary" onClick={() => handleViewTranscription(transcription)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}
      >
        <Typography variant="h4" component="h1">
          Transcriptions Management
        </Typography>
      </div>

      {isLoadingData ? (
        <Loading />
      ) : (
        <SortableTable
          data={transcriptions}
          headCells={headCells}
          defaultSortBy="creationDate"
          defaultOrder="desc"
          idAccessor={transcription => transcription.id}
          emptyMessage="No transcriptions found"
        />
      )}

      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>Transcription Details</DialogTitle>
        <DialogContent>
          {selectedTranscription && (
            <Paper style={{ padding: '20px' }}>
              <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                {selectedTranscription.content}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TranscriptionsManagement;
